import { WORK_ASSIGNMENT_ERROR_LABELS } from 'utils/assignmentCustomErrorGenerator';

/**
 * `BreakdownValidationError` codes that reach a user in practice. Everything
 * else is either mutually-exclusive-combos wizardry (which the flow's own
 * validators already block) or backend guards on ids the wizard would never
 * submit — those fall back to the raw code so a QA / support ticket still has
 * a fingerprint to grep.
 */
export const BREAKDOWN_ERROR_LABELS = {
	invalidBreakdownStation: 'The selected breakdown location is not valid for this segment.',
	inboundTravelingBeforeIncident:
		'Replacement inbound traveling cannot start before the incident time.',
	actualSegmentAtBreakdown:
		'The chosen segment is already the actual segment for the reported incident.',
	multipleReplacementSources: 'Only one replacement source can be used at a time.',
	missingReplacementCrewMember: 'A replacement crew member is required for this breakdown.',
	incidentBeforeSequenceStart: 'The incident time cannot be before the sequence starts.',
	drivingBeforeIncident: 'Post-incident driving cannot start before the incident time.',
	absenceBeforeIncident: 'Driver absence cannot start before the incident time.',
	outboundTravelingBeforeIncident: 'Cushioning travelling cannot start before the incident time.',
	parallelRideWithDriving: 'A parallel ride cannot be combined with post-incident driving.',
	absenceWithOutboundTraveling: 'Driver absence cannot be combined with cushioning travelling.',
	incidentBeforeSegmentDeparture: 'The incident time cannot be before the segment departure time.',
};

/**
 * Flatten both error arrays from `BreakdownErrorResponse` into a single list
 * of string codes, in the order they arrived. Missing arrays are treated as
 * empty. Non-string entries fall back to the whole object stringified.
 *
 * @param {{
 *   errors?: Array,
 *   workAssignmentErrors?: Array,
 * }} response
 * @returns {string[]}
 */
export const flattenBreakdownErrors = (response = {}) => {
	const normalize = (list = []) =>
		list.map((entry) =>
			typeof entry === 'string' ? entry : (entry?.code ?? JSON.stringify(entry))
		);

	return [...normalize(response.errors), ...normalize(response.workAssignmentErrors)];
};

/**
 * Build the notification for `notificationStore.setNotification`. Labels
 * resolve against `BREAKDOWN_ERROR_LABELS` first, then
 * `WORK_ASSIGNMENT_ERROR_LABELS`, then fall back to the raw code.
 *
 * @param {string[]} errorList
 * @returns {{ label: string[], header: string, appearance: 'danger' }}
 */
export const createBreakdownErrorNotification = (errorList) => ({
	label: errorList.map(
		(code) => BREAKDOWN_ERROR_LABELS[code] ?? WORK_ASSIGNMENT_ERROR_LABELS[code] ?? code
	),
	header: 'Issue creating the breakdown:',
	appearance: 'danger',
});
